import {memo, useEffect, useMemo, useRef, type CSSProperties} from "react";
import {token} from "@atlaskit/tokens";
import {GenericModal} from "~/components/genericModal";
import type {EventColorMap} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import type {CalendarDaySummaryModalPresentation} from "~/components/beatPlanningCalendar/calendarDaySummaryModalPresentation";
import {formatTimeFromJsDate} from "~/utilities/utilities";

type CalendarSelectableEvent = {
    id: string;
    title: string;
    start: Date | null;
    end: Date | null;
    allDay: boolean;
    extendedProps: unknown;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
};

function allDayStatusChipOnGreenTileStyle(
    statusType: string | undefined,
    getEventColor: ((type: string | undefined) => EventColorMap) | undefined,
): CSSProperties | undefined {
    if (statusType === undefined || getEventColor === undefined) {
        return undefined;
    }
    const colors = getEventColor(statusType);
    if (
        colors.backgroundColor === undefined &&
        colors.borderColor === undefined &&
        colors.textColor === undefined
    ) {
        return undefined;
    }
    return {
        backgroundColor: colors.backgroundColor ?? token("color.background.neutral"),
        borderColor: colors.borderColor ?? token("color.border"),
        color: colors.textColor ?? token("color.text"),
    };
}

function MemoizedCalendarDayEventsModal({
    opened,
    title,
    selectedPeriodEvents,
    onClose,
    onEventSelected,
    getEventColor,
    allDayStyle,
    presentation,
    hasMore = false,
    isLoadingMore = false,
    error = null,
    onRetry,
    onLoadMore,
}: {
    opened: boolean;
    title: string;
    selectedPeriodEvents: Array<CalendarSelectableEvent>;
    onClose: () => void;
    onEventSelected: (event: CalendarSelectableEvent) => void;
    getEventColor: ((type: string | undefined) => EventColorMap) | undefined;
    allDayStyle: {mode: "fixedGreen" | "inheritStatus"};
    presentation?: CalendarDaySummaryModalPresentation;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    error?: string | null;
    onRetry?: () => void;
    onLoadMore?: () => void;
}) {
    const isEmotoradMySalesTeamPresentation = presentation?.kind === "emotoradMySalesTeam";
    const listRef = useRef<HTMLDivElement | null>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (opened !== true) {
            return;
        }
        if (hasMore !== true || onLoadMore === undefined || isLoadingMore === true || error !== null) {
            return;
        }
        const sentinel = loadMoreSentinelRef.current;
        const root = listRef.current;
        if (sentinel === null || root === null) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry === undefined || entry.isIntersecting !== true) {
                    return;
                }
                onLoadMore();
            },
            {
                root: root,
                rootMargin: "96px",
                threshold: 0,
            },
        );
        observer.observe(sentinel);
        return () => {
            observer.disconnect();
        };
    }, [error, hasMore, isLoadingMore, onLoadMore, opened, selectedPeriodEvents.length]);

    const selectedDayStatusSummary = useMemo(() => {
        if (isEmotoradMySalesTeamPresentation === true) {
            return [];
        }
        const summaryMap = new Map<
            string,
            {
                label: string;
                count: number;
                backgroundColor?: string;
                textColor?: string;
            }
        >();
        selectedPeriodEvents.forEach((event) => {
            const extendedProps = event.extendedProps as {type?: string; typeLabel?: string} | undefined;
            const statusType = extendedProps?.type;
            const statusLabel = extendedProps?.typeLabel ?? statusType;
            if (statusType === undefined || statusLabel === undefined) {
                return;
            }
            const existing = summaryMap.get(statusType);
            if (existing !== undefined) {
                existing.count += 1;
                return;
            }
            const typeColors = getEventColor?.(statusType);
            summaryMap.set(statusType, {
                label: statusLabel,
                count: 1,
                backgroundColor: typeColors?.backgroundColor ?? event.backgroundColor,
                textColor: typeColors?.textColor ?? event.textColor,
            });
        });
        return Array.from(summaryMap.values());
    }, [selectedPeriodEvents, getEventColor, isEmotoradMySalesTeamPresentation]);

    const entityCountLabel = (() => {
        const n = selectedPeriodEvents.length;
        const moreSuffix = hasMore === true ? "+" : "";
        if (presentation?.kind === "emotoradMySalesTeam") {
            const {entitySingularNoun, entityPluralNoun} = presentation;
            return `${n}${moreSuffix} ${n === 1 && hasMore !== true ? entitySingularNoun : entityPluralNoun}`;
        }
        return `${n}${moreSuffix} ${n === 1 && hasMore !== true ? "event" : "events"}`;
    })();

    const showInitialLoading = selectedPeriodEvents.length === 0 && isLoadingMore === true;

    return (
        <GenericModal
            opened={opened}
            onClose={onClose}
            title={title}
            size="lg"
            closeOnClickOutside={true}
            className={
                isEmotoradMySalesTeamPresentation === true
                    ? "beat-planning-calendar__day-events-modal-shell beat-planning-calendar__day-events-modal-shell--emotorad-my-sales-team"
                    : "beat-planning-calendar__day-events-modal-shell"
            }
        >
            <div
                className={
                    isEmotoradMySalesTeamPresentation === true
                        ? "beat-planning-calendar__day-events-modal beat-planning-calendar__day-events-modal--emotorad-my-sales-team"
                        : "beat-planning-calendar__day-events-modal"
                }
            >
                <div className="beat-planning-calendar__day-events-summary-row">
                    <div className="beat-planning-calendar__day-events-count">{entityCountLabel}</div>
                    {selectedDayStatusSummary.length > 0 ? (
                        <div className="beat-planning-calendar__day-events-status-summary">
                            {selectedDayStatusSummary.map((item) => (
                                <span
                                    key={item.label}
                                    className="beat-planning-calendar__day-events-status-pill"
                                    style={{
                                        backgroundColor: item.backgroundColor,
                                        color: item.textColor,
                                    }}
                                >
                                    {item.label} ({item.count})
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="beat-planning-calendar__day-events-list" ref={listRef}>
                    {showInitialLoading === true ? (
                        <div className="beat-planning-calendar__day-events-loading">Loading…</div>
                    ) : null}
                    {error !== null ? (
                        <div role="alert" className="beat-planning-calendar__day-events-loading">
                            <div>{error}</div>
                            {onRetry === undefined ? null : (
                                <button type="button" className="underline" onClick={onRetry}>Retry</button>
                            )}
                        </div>
                    ) : null}
                    {selectedPeriodEvents.map((event, index) => {
                        const start = event.start;
                        const end = event.end;
                        const extendedProps = event.extendedProps as
                            | {
                                  type?: string;
                                  typeLabel?: string;
                                  templateLabel?: string;
                                  templateStrokeColor?: string;
                                  rawRow?: {
                                      btlActivityTemplateName?: string;
                                  };
                              }
                            | undefined;
                        const statusType = extendedProps?.type;
                        const statusLabel = extendedProps?.typeLabel;
                        const typeColors = getEventColor?.(statusType);
                        const templateLabel =
                            extendedProps?.templateLabel ?? extendedProps?.rawRow?.btlActivityTemplateName;
                        const templateStrokeColor = extendedProps?.templateStrokeColor;
                        const isAllDayEvent = event.allDay === true;
                        const cardBackgroundColor =
                            isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                ? token("color.icon.accent.green")
                                : event.backgroundColor ?? typeColors?.backgroundColor;
                        const cardTextColor =
                            isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                ? token("color.text.inverse")
                                : event.textColor ?? typeColors?.textColor;
                        const cardBorderColor =
                            isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                ? token("color.icon.accent.green")
                                : event.borderColor ?? typeColors?.borderColor;
                        const allDayStatusChipStyle =
                            isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                ? allDayStatusChipOnGreenTileStyle(statusType, getEventColor)
                                : undefined;
                        const timeLabel =
                            isAllDayEvent === true
                                ? "All day"
                                : start !== null
                                  ? `${formatTimeFromJsDate(start)}${
                                        end !== null ? ` - ${formatTimeFromJsDate(end)}` : ""
                                    }`
                                  : "";
                        const rawRow = extendedProps?.rawRow;
                        const secondaryLine =
                            presentation?.kind === "emotoradMySalesTeam" && rawRow !== undefined
                                ? presentation.getSecondaryLine(rawRow)
                                : "";
                        const emotoradNameContactLine =
                            secondaryLine.trim() !== ""
                                ? `${event.title}, ${secondaryLine}`
                                : event.title;
                        return (
                            <button
                                key={`${event.id}-${start?.toISOString() ?? "na"}`}
                                type="button"
                                className={
                                    isEmotoradMySalesTeamPresentation === true
                                        ? `beat-planning-calendar__day-events-item beat-planning-calendar__day-events-item--emotorad-my-sales-team beat-planning-calendar__day-events-item--emotorad-my-sales-team--${
                                              index % 2 === 0 ? "even" : "odd"
                                          }`
                                        : "beat-planning-calendar__day-events-item"
                                }
                                onClick={() => {
                                    onClose();
                                    onEventSelected(event);
                                }}
                                style={
                                    isEmotoradMySalesTeamPresentation === true
                                        ? undefined
                                        : {
                                              backgroundColor: cardBackgroundColor,
                                              color: cardTextColor,
                                              borderColor: cardBorderColor,
                                          }
                                }
                            >
                                <span className="beat-planning-calendar__day-events-item-line">
                                    <span>{timeLabel}</span>
                                    <span>&bull;</span>
                                    <span>
                                        {isEmotoradMySalesTeamPresentation === true
                                            ? emotoradNameContactLine
                                            : event.title}
                                    </span>
                                </span>
                                {isEmotoradMySalesTeamPresentation === true ? null : isAllDayEvent === true || templateLabel !== undefined ? (
                                    <span
                                        className={
                                            isAllDayEvent === true
                                                ? "beat-planning-calendar__day-events-item-chips beat-planning-calendar__day-events-item-chips--inline"
                                                : "beat-planning-calendar__day-events-item-chips"
                                        }
                                    >
                                        {isAllDayEvent === true &&
                                        statusLabel !== undefined &&
                                        allDayStyle.mode === "fixedGreen" ? (
                                            <span
                                                className="beat-planning-calendar__schedule-event-chip"
                                                style={allDayStatusChipStyle}
                                            >
                                                {statusLabel}
                                            </span>
                                        ) : null}
                                        {templateLabel !== undefined ? (
                                            <span
                                                className="beat-planning-calendar__schedule-event-chip"
                                                style={{borderColor: templateStrokeColor}}
                                            >
                                                {templateLabel}
                                            </span>
                                        ) : null}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                    {hasMore === true && onLoadMore !== undefined && error === null ? (
                        <div
                            ref={loadMoreSentinelRef}
                            className="beat-planning-calendar__day-events-load-more-sentinel"
                            aria-hidden={true}
                        />
                    ) : null}
                    {isLoadingMore === true && selectedPeriodEvents.length > 0 ? (
                        <div className="beat-planning-calendar__day-events-loading">Loading more…</div>
                    ) : null}
                </div>
            </div>
        </GenericModal>
    );
}

export const CalendarDayEventsModal = memo(MemoizedCalendarDayEventsModal);
